import React, { useEffect, useState } from "react";
import { Control, Controller, UseFormSetValue, UseFormWatch } from "react-hook-form";

import { Button, FormGroup, Input, LabelPlacement } from "@cofano/cck";
import { FormGroupOverflow } from "@cofano/cck/FormGroup/utils";
import { Box, Grid2 as Grid, Typography } from "@mui/material";

import { ProductPickerDialog } from "components/products/ProductPicker";
import { FormErrorMessage } from "components/form/FormErrorMessage";

import { DataFormat, Language } from "utils";

import { ProductFormFields, ProductTypes } from "view/products/utils/product.types";
import { ProductTankerType } from "view/products/components/form/fields/ProductTankerType";
import { ProductCargoTankDesignSelect } from "view/products/components/form/fields/ProductCargoTankDesign";
import { ProductCargoTankTypeSelect } from "view/products/components/form/fields/ProductCargoTankType";
import { ProductCargoTankEquipmentSelect } from "view/products/components/form/fields/ProductCargoTankEquipment";
import { ProductSamplingTypeSelect } from "view/products/components/form/fields/ProductSamplingType";
import { ProductTemperatureClass } from "view/products/components/form/fields/ProductTemperatureClass";
import { ProductEquipment } from "view/products/components/form/fields/ProductEquipment";
import { RangedInput } from "view/products/components/form/ProductGeneral";
import { headerMap } from "view/products/utils/ProductUtils";
import { ProductSchemaInput } from "view/products/utils/product.schema";

import { AdnGoodFragment } from "__generated__/view/products/graphql/product.fragments.generated";

const ProductDangerousGoods = ({ control, watch, adnGoods, setValue, product }: {
	control: Control<ProductSchemaInput>;
	watch: UseFormWatch<ProductSchemaInput>;
	adnGoods: AdnGoodFragment[];
	setValue: UseFormSetValue<ProductSchemaInput>;
	product: Pick<ProductFormFields, "adnGood"> | null;
}) => {

	const [ dialogOpen, setDialogOpen ] = useState(false);
	const [ adnGood, setAdnGood ] = useState(product?.adnGood || null);

	const [ isDangerousGood, isValidated, density, densityMin, densityMax ] = watch([ "isDangerousGood", "isValidated", "density", "densityKgM3Min", "densityKgM3Max" ]);

	const setAdnGoodData = (good: AdnGoodFragment) => {
		setValue("properShippingNameNL", good.properShippingNameNL || "");
		setValue("properShippingNameEN", good.properShippingNameEN || "");
		setValue("properShippingNameDE", good.properShippingNameDE || "");
		setValue("properShippingNameFR", good.properShippingNameFR || "");
		setValue("density", good.density || "");
		setValue("densityTemperature", Number(good.densityTemperature) || undefined);
		setValue("adnGoodInput.tankerType", good.tankerType || "");
		setValue("adnGoodInput.cargoTankDesign", good.cargoTankDesign || "");
		setValue("adnGoodInput.cargoTankType", good.cargoTankType || "");
		setValue("adnGoodInput.cargoTankEquipment", good.cargoTankEquipment?.split(",") || []);
		setValue("adnGoodInput.samplingType", good.samplingType || "");
		setValue("adnGoodInput.temperatureClass", good.temperatureClass || "");
		setValue("adnGoodInput.requiredEquipment", good.requiredEquipment?.split(",") || []);
		setValue("adnGoodInput.hazardCode", good.hazards || "");
		setValue("adnGoodInput.openingPressureRelieveValve", good.openingPressureRelieveValve || "");
		setValue("adnGoodInput.maxFillingDegreePercentage", good.maxFillingDegreePercentage || "");
		setValue("adnGoodInput.explosionGroup", good.explosionGroup || "");
		setValue("productADNGoodId", good.id);
	};

	const onSelectAdnGood = (good: AdnGoodFragment | undefined) => {
		if (good) {
			setAdnGood(good);
			setAdnGoodData(good);
		}
	};

	const onRemoveAdnGood = () => {
		setAdnGood(null);
		setValue("adnGoodInput", undefined);
		setValue("properShippingNameNL", "");
		setValue("properShippingNameEN", "");
		setValue("properShippingNameDE", "");
		setValue("properShippingNameFR", "");
		setValue("density", "");
		setValue("densityTemperature", undefined);
		setValue("productADNGoodId", undefined);
		setValue("isValidated", false);
	};

	useEffect(() => {
		if (!isDangerousGood) {
			onRemoveAdnGood();
		}
	}, [ isDangerousGood ]);


	const optionalFormGroupProps = {
		overflow: "visible" as FormGroupOverflow,
		labelPlacement: "left-center" as LabelPlacement,
		labelWidth: 300,
	};

	return (
		<Grid container spacing={2}>
			{adnGood ? (
				<Grid size={{ xs: 12 }}>
					<FormGroup label={Language("PRODUCT_ADN_ADN_CODE")} {...optionalFormGroupProps}>
						{adnGood.adnCode || "-"}
					</FormGroup>

					<FormGroup label={headerMap["unNumber"]} {...optionalFormGroupProps}>
						{adnGood.unNumber || "-"}
					</FormGroup>

					<FormGroup label={headerMap["properShippingNameEN"]} {...optionalFormGroupProps} required>
						<Controller
							name={"properShippingNameEN"}
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={headerMap["properShippingNameNL"]} {...optionalFormGroupProps} required>
						<Controller
							name={"properShippingNameNL"}
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={headerMap["properShippingNameDE"]} {...optionalFormGroupProps} required>
						<Controller
							name={"properShippingNameDE"}
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={headerMap["properShippingNameFR"]} {...optionalFormGroupProps} required>
						<Controller
							name={"properShippingNameFR"}
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={headerMap["classNumber"]} {...optionalFormGroupProps}>
						{adnGood.classNumber || "-"}
					</FormGroup>

					<FormGroup label={headerMap["classificationCode"]} {...optionalFormGroupProps}>
						{adnGood.classificationCode || "-"}
					</FormGroup>

					<FormGroup label={headerMap["packingGroup"]} {...optionalFormGroupProps}>
						{adnGood.packingGroup || "-"}
					</FormGroup>

					<FormGroup label={headerMap["hazards"]} {...optionalFormGroupProps} required={isValidated}>
						<Controller
							name="adnGoodInput.hazardCode"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={headerMap["tankerType"]} {...optionalFormGroupProps}>
						<Controller
							name="adnGoodInput.tankerType"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<ProductTankerType {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={headerMap["cargoTankDesign"]} {...optionalFormGroupProps} required={isValidated}>
						<Controller
							name="adnGoodInput.cargoTankDesign"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<ProductCargoTankDesignSelect {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={headerMap["cargoTankType"]} {...optionalFormGroupProps} required={isValidated}>
						<Controller
							name="adnGoodInput.cargoTankType"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<ProductCargoTankTypeSelect {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={headerMap["cargoTankEquipment"]} {...optionalFormGroupProps}>
						<Controller
							name="adnGoodInput.cargoTankEquipment"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<ProductCargoTankEquipmentSelect {...field} multiple />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={headerMap["openingPressureRelieveValve"]} {...optionalFormGroupProps} required={isValidated}>
						<Controller
							name="adnGoodInput.openingPressureRelieveValve"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} addonAfter="kPa" />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={headerMap["maxFillingDegreePercentage"]} {...optionalFormGroupProps} required={isValidated}>
						<Controller
							name="adnGoodInput.maxFillingDegreePercentage"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} addonAfter="%" />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<RangedInput
						name="density"
						nameMin="densityKgM3Min"
						addonAfter={DataFormat.UNITS.DENSITY}
						nameMax="densityKgM3Max"
						control={control}
						setValue={setValue}
						label={headerMap["density"]}
						labelWidth={optionalFormGroupProps.labelWidth}
						watch={watch}
						step=".0001"
					/>

					<FormGroup label={headerMap["densityTemperature"]} {...optionalFormGroupProps}>
						<Controller
							name={"densityTemperature"}
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input
										{...field}
										value={field.value as string}
										disabled={!(density || densityMin || densityMax)}
										addonAfter={"°C"} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={headerMap["samplingType"]} {...optionalFormGroupProps} required={isValidated}>
						<Controller
							name="adnGoodInput.samplingType"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<ProductSamplingTypeSelect {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={headerMap["permissiblePumpChamberUnderDeck"]} {...optionalFormGroupProps}>
						{DataFormat.booleanAsText(adnGood.permissiblePumpChamberUnderDeck)}
					</FormGroup>

					<FormGroup label={headerMap["temperatureClass"]} {...optionalFormGroupProps}>
						<Controller
							name="adnGoodInput.temperatureClass"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<ProductTemperatureClass {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={headerMap["explosionGroup"]} {...optionalFormGroupProps}>
						<Controller
							name="adnGoodInput.explosionGroup"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<Input {...field} value={field.value ?? ""} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={headerMap["explosionProtectionRequired"]} {...optionalFormGroupProps}>
						{DataFormat.booleanAsText(adnGood.explosionProtectionRequired) || "-"}
					</FormGroup>

					<FormGroup label={headerMap["requiredEquipment"]} {...optionalFormGroupProps}>
						<Controller name="adnGoodInput.requiredEquipment"
							control={control}
							render={({ field, formState }) => (
								<React.Fragment>
									<ProductEquipment {...field} />
									<FormErrorMessage name={field.name} errors={formState.errors} />
								</React.Fragment>
							)} />
					</FormGroup>

					<FormGroup label={headerMap["numberOfBlueConesOrLights"]} {...optionalFormGroupProps}>
						{adnGood.numberOfBlueConesOrLights || "-"}
					</FormGroup>

					<FormGroup label={headerMap["extraRequirements"]} {...optionalFormGroupProps}>
						{adnGood.extraRequirements || "-"}
					</FormGroup>

				</Grid>
			) : (<Grid size={{ xs: 12 }}><Typography component="span">{Language("PRODUCTS_NO_ADN_PRODUCT_LINKED")}</Typography></Grid>)}

			<Grid size={{ xs: 12 }}>
				<Box sx={{ display: "flex", justifyContent: "end", alignItems: "center" }}>
					<Box marginRight={2}>
						<Button disabled={!isDangerousGood || adnGood != null} onClick={() => setDialogOpen(true)}>
							{Language("PRODUCTS_ADD_DANGEROUS_GOOD")}
						</Button>

						<ProductPickerDialog
							open={dialogOpen}
							onClose={() => setDialogOpen(false)}
							onChange={(good) => onSelectAdnGood(good)}
							type={ProductTypes.ADN}
							list={adnGoods} />
					</Box>

					<Button onClick={() => onRemoveAdnGood()} disabled={!adnGood}>
						{Language("PRODUCTS_REMOVE_DANGEROUS_GOOD")}
					</Button>
				</Box>
			</Grid>
		</Grid>
	);
};

export default ProductDangerousGoods;
